'use client';

import { c as _c } from "react/compiler-runtime";
import type { RunnerConnectionKind } from '@shared/all/domains/socket';
import formatRelativeTime from '@shared/utils/helpers/formatRelativeTime';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import useSocket from '@shared/utils/hooks/useSocket';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Cloud, Copy, GitBranch, ShieldCheck, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useRunners } from '../../hooks/useRunners';
import { RUNNER_START_COMMAND as START_COMMAND, runnerDisconnectMessage } from '../../utils/runnerCopy';
import { client } from '../../utils/trpc';
import Confirmation from '../common/Confirmation';
import { DisconnectButton, IntegrationRow, IntegrationSection } from './IntegrationList';

// Known private-connection kinds → label + icon. Unknown kinds (a newer runner
// reporting something this client doesn't know yet) fall back to the raw string.
const CONNECTION_META: Record<RunnerConnectionKind, {
  label: string;
  icon: typeof Cloud;
}> = {
  claude: {
    label: 'Claude',
    icon: Sparkles
  },
  onedrive: {
    label: 'OneDrive',
    icon: Cloud
  },
  git: {
    label: 'Git',
    icon: GitBranch
  }
};
const ConnectionBadge = t0 => {
  const $ = _c(5);
  const {
    kind
  } = t0;
  const meta = CONNECTION_META[kind as RunnerConnectionKind];
  const Icon = meta?.icon ?? ShieldCheck;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon size={12} className="text-primary" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = meta?.label ?? kind;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <span className="inline-flex items-center gap-1 rounded-full border border-ds-border-standard px-2 py-0.5 text-[11px] font-emphasis text-textSecondary">{t1}{t2}</span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const RunnersList = () => {
  const $ = _c(32);
  const queryClient = useQueryClient();
  const {
    onRunnerConnectionsUpdated
  } = useSocket();
  const [runnerToDisconnect, setRunnerToDisconnect] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [liveConnections, setLiveConnections] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["runners"],
      queryFn: _temp,
      retry: false
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: runnersData,
    isLoading
  } = useQuery(t1);
  const {
    runners: onlineRunners,
    sessions
  } = useRunners();
  let t2;
  if ($[2] !== onlineRunners) {
    t2 = new Set(onlineRunners.map(_temp2));
    $[2] = onlineRunners;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const onlineRunnerIds = t2;
  let t3;
  if ($[4] !== onRunnerConnectionsUpdated) {
    t3 = () => onRunnerConnectionsUpdated(t4 => {
      const {
        runnerId,
        connections
      } = t4;
      setLiveConnections(prev => ({
        ...prev,
        [runnerId]: connections
      }));
    });
    $[4] = onRunnerConnectionsUpdated;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== runnerToDisconnect || $[8] !== sessions) {
    const disconnectJobCount = runnerToDisconnect ? sessions.filter(s => s.runnerId === runnerToDisconnect.id).length : 0;
    t5 = runnerDisconnectMessage(disconnectJobCount);
    $[7] = runnerToDisconnect;
    $[8] = sessions;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const disconnectMessage = t5;
  let t6;
  if ($[10] !== queryClient || $[11] !== runnerToDisconnect) {
    t6 = async () => {
      if (!runnerToDisconnect) {
        return;
      }
      const [error] = await client.runners.disconnect.mutate({
        id: runnerToDisconnect.id
      });
      if (!error) {
        toast(`Disconnected "${runnerToDisconnect.name}".`);
        await queryClient.invalidateQueries({
          queryKey: ["runners"]
        });
      }
      setRunnerToDisconnect(null);
    };
    $[10] = queryClient;
    $[11] = runnerToDisconnect;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleDisconnect = t6;
  const copyStartCommand = _temp3;
  const section = _temp4;
  if (isLoading) {
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = section(<IntegrationRow icon={<Bot size={18} />} name="Runners" status={"Checking\u2026"} />);
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  if (!runnersData?.runners.length) {
    let t7;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Bot size={18} />;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = section(<IntegrationRow icon={t7} tint="bg-background-primary text-textSecondary" name="No runners yet" status={<>Run{" "}<code className="rounded bg-background-primary px-1 py-0.5 text-textPrimary">factiii-runner connect</code>{" "}on a machine to pair it as a Board AI agent.</>} />);
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    return t8;
  }
  let t7;
  if ($[16] !== liveConnections || $[17] !== onlineRunnerIds || $[18] !== runnersData.runners || $[19] !== sessions) {
    let t8;
    if ($[21] !== liveConnections || $[22] !== onlineRunnerIds || $[23] !== sessions) {
      t8 = runner => {
        const isOnline = onlineRunnerIds.has(runner.id);
        const runnerSessions = sessions.filter(s_0 => s_0.runnerId === runner.id);
        const jobCount = runnerSessions.length;
        const last = runner.lastActiveAt ? formatRelativeTime(new Date(runner.lastActiveAt)) : "never";
        const connections_0 = liveConnections[runner.id] ?? runner.reportedConnections ?? [];
        const activity = isOnline ? jobCount > 0 ? `${jobCount} job${jobCount === 1 ? "" : "s"} running` : "Online now" : `Last active ${last}`;
        return <IntegrationRow key={runner.id} icon={<Bot size={18} />} name={runner.name} dot={isOnline ? "online" : "offline"} status={`${runner.platform} · ${activity}`} actions={<div className="flex items-center gap-2"><DisconnectButton label={runner.name} onClick={() => setRunnerToDisconnect({
            id: runner.id,
            name: runner.name
          })} /></div>}>{(connections_0.length > 0 || jobCount > 0 || !isOnline) && <div className="space-y-2">{connections_0.length > 0 && <div className="flex flex-wrap items-center gap-1.5">{connections_0.map(_temp5)}</div>}{isOnline && jobCount > 0 && <ul className="space-y-1.5 rounded-lg border border-ds-border-subtle bg-background-primary/50 p-3">{runnerSessions.map(_temp6)}</ul>}{!isOnline && <div className="flex flex-wrap items-center gap-2 text-xs text-textSecondary"><span>To bring it back, run on that machine:</span><code className="rounded bg-background-primary px-1.5 py-0.5 text-textPrimary">{START_COMMAND}</code><button onClick={copyStartCommand} className="flex items-center gap-1 rounded px-1.5 py-0.5 text-primary hover:bg-background-primary" aria-label="Copy start command"><Copy size={12} />Copy</button></div>}</div>}</IntegrationRow>;
      };
      $[21] = liveConnections;
      $[22] = onlineRunnerIds;
      $[23] = sessions;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    t7 = section(runnersData.runners.map(t8));
    $[16] = liveConnections;
    $[17] = onlineRunnerIds;
    $[18] = runnersData.runners;
    $[19] = sessions;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[25] !== disconnectMessage || $[26] !== handleDisconnect || $[27] !== runnerToDisconnect) {
    t8 = runnerToDisconnect && <Confirmation title={`Disconnect "${runnerToDisconnect.name}"?`} message={disconnectMessage} cancelHandler={() => setRunnerToDisconnect(null)} confirmHandler={handleDisconnect} actionButtonText="Disconnect" variant="DANGER" />;
    $[25] = disconnectMessage;
    $[26] = handleDisconnect;
    $[27] = runnerToDisconnect;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t7 || $[30] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
};
export default RunnersList;
function _temp() {
  return unwrapFromResult(client.runners.list.query());
}
function _temp2(r) {
  return r.runnerId;
}
async function _temp3() {
  try {
    await navigator.clipboard.writeText(START_COMMAND);
    toast("Copied \u2014 run it on the runner machine.");
  } catch {
    toast("Could not copy to clipboard", "", "error");
  }
}
function _temp4(children) {
  return <IntegrationSection title="Private connections" description={"Board AI agents run on your machines. Any OneDrive, Claude, or Git they connect stays on that device \u2014 Factiii\u2019s servers never receive those credentials."}>{children}</IntegrationSection>;
}
function _temp5(kind) {
  return <ConnectionBadge key={kind} kind={kind} />;
}
function _temp6(s_1) {
  return <li key={`${s_1.spaceSlug}::${s_1.postId}`} className="flex items-center gap-2 text-xs text-textPrimary"><span className="size-1.5 shrink-0 animate-pulse rounded-full bg-green-500" /><span className="truncate">{s_1.taskTitle}</span><span className="ml-auto shrink-0 text-textSecondary">{s_1.spaceSlug}</span></li>;
}